import { useEventListener } from '@vueuse/core'
import { computed, type MaybeRefOrGetter, type Ref, toValue } from 'vue'

/** A touch screen (a phone, a tablet): the calendar alone, no keyboard over it. */
export const touchScreen = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches)

/**
 * Typing in a PrimeVue date field (`AppDatePicker`, `AppDateRangePicker`, 2026-11). The listeners go on the field's
 * root in the capture phase (PrimeVue does not pass capture listeners on). A keystroke stops there: PrimeVue's own
 * reading of the text keeps a date of its own ("2/11/26" → the year 26) and redraws the field empty, so the host reads
 * it: `read(text, false)` on each keystroke, `read(text, true)` once the user is done (Enter, Tab, leaving the field).
 * The text is the last one typed — closing the calendar redraws the input before its blur. Nothing typed since the
 * field took focus: nothing is read.
 */
export function useDateTyping(
  picker: Ref<{ $el: HTMLElement } | null>,
  enabled: MaybeRefOrGetter<boolean>,
  read: (text: string, done: boolean) => void,
): void {
  const root = computed(() => picker.value?.$el ?? null)
  let typed: string | null = null
  const fromInput = (event: Event) => (event.target as HTMLElement | null)?.tagName === 'INPUT'

  useEventListener(
    root,
    'input',
    (event: Event) => {
      if (!toValue(enabled) || !fromInput(event)) return
      event.stopPropagation()
      typed = (event.target as HTMLInputElement).value
      read(typed, false)
    },
    { capture: true },
  )
  useEventListener(
    root,
    'keydown',
    (event: KeyboardEvent) => {
      if ((event.key === 'Enter' || event.key === 'Tab') && fromInput(event) && typed !== null) read(typed, true)
    },
    { capture: true },
  )
  useEventListener(root, 'focusout', (event: FocusEvent) => {
    if (!fromInput(event) || typed === null) return
    read(typed, true)
    typed = null
  })
}
